if (!customElements.get('bedding-offer')) {
  class BeddingOffer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.toggle = this.querySelector('input[type="checkbox"]');
      try {
        this.data = JSON.parse(this.querySelector('[data-bedding-data]').textContent);
        this.section = this.closest('.shopify-section');
        const sync = () => this.resolve();
        this.section.addEventListener('on:variant:change', sync, { signal: this.controller.signal });
        this.section.addEventListener('change', (event) => {
          if (event.target.name === 'id') sync();
        }, { signal: this.controller.signal });
        this.setupAddButton();
        this.resolve();
        this.setupInfoDrawer();
      } catch (error) {
        this.disable();
      }
    }

    disconnectedCallback() {
      this.closeInfoDrawer();
      this.drawer?.remove();
      this.drawer = null;
      this.syncDrawer = null;
      this.controller?.abort();
      this.controller = null;
      this.renderedState = null;
    }

    disable() {
      this.renderedState = null;
      this.closeInfoDrawer();
      this.wasFree = false;
      this.hidden = true;
      this.toggle.checked = false;
      this.toggle.disabled = true;
      const message = this.querySelector('[data-bedding-message]');
      message.textContent = message.dataset.message;
      message.hidden = false;
      this.syncAddButton();
      return null;
    }

    // The add button replaces the old switch; the hidden checkbox stays the source of truth.
    setupAddButton() {
      this.addButton = this.querySelector('[data-bedding-add]');
      if (!this.addButton) return;
      const { signal } = this.controller;
      this.addButton.addEventListener('click', () => {
        if (this.toggle.disabled) return;
        this.toggle.checked = !this.toggle.checked;
        this.toggle.dispatchEvent(new Event('change', { bubbles: true }));
      }, { signal });
      this.toggle.addEventListener('change', () => this.syncAddButton(), { signal });
    }

    syncAddButton() {
      const button = this.addButton;
      if (!button) return;
      const added = this.toggle.checked;
      button.disabled = this.toggle.disabled;
      button.setAttribute('aria-pressed', String(added));
      const labels = button.dataset;
      const free = this.hasAttribute('data-free') && labels.freeLabelOff;
      button.querySelector('[data-bedding-add-text]').textContent = free
        ? (added ? labels.freeLabelOn : labels.freeLabelOff)
        : (added ? labels.labelOn : labels.labelOff);
      // product-form.js adds getSelectedPrice() to the Add to Cart label price.
      document.getElementById(this.dataset.formId)?.closest('product-form')?.scheduleAtcPriceUpdate?.();
    }

    // Displayed offer price of the selected, paid bundle in cents; 0 when not selected or FREE.
    getSelectedPrice() {
      if (this.hidden || !this.toggle?.checked || this.hasAttribute('data-free')) return 0;
      const text = this.querySelector('[data-bedding-offer-price]')?.textContent || '';
      const amount = Number.parseFloat(text.replace(/[^0-9.]/g, ''));
      return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
    }

    setupInfoDrawer() {
      const opener = this.querySelector('[data-bedding-info]');
      const template = this.querySelector('[data-bedding-drawer-template]');
      if (!template || !opener) return;
      const { signal } = this.controller;
      opener.addEventListener('click', async () => {
        if (opener.disabled) return;
        opener.disabled = true;
        opener.setAttribute('aria-busy', 'true');
        try {
          const { openDrawer } = await import(template.dataset.script);
          if (!signal.aborted && this.isConnected && !this.hidden) {
            openDrawer(this, template, opener, signal);
          }
        } catch (error) {
          console.error('Bedding information could not be loaded.', error);
        } finally {
          opener.disabled = false;
          opener.removeAttribute('aria-busy');
        }
      }, { signal });
    }

    closeInfoDrawer() {
      if (this.drawer?.hasAttribute('open')) this.drawer.close();
    }

    updatePrices(bundle, size, isFree = false) {
      const normalized = this.normalizeSize(size);
      const isSinglePillow = normalized === 'twin' || normalized === 'twin xl';
      const pillows = this.querySelector('[data-bedding-pillows]');
      if (pillows) pillows.textContent = isSinglePillow ? pillows.dataset.singleLabel : pillows.dataset.multipleLabel;
      const isSplitKing = normalized === 'split king';
      const protector = this.querySelector('[data-bedding-protector]');
      if (protector) protector.textContent = isSplitKing ? protector.dataset.doubleLabel : protector.dataset.baseLabel;
      for (const item of ['pillows', 'protector', 'sheets']) {
        const value = this.querySelector(`[data-bedding-value="${item}"]`);
        if (value) value.textContent = isSinglePillow ? value.dataset.twinValue : isSplitKing ? value.dataset.splitValue : value.dataset.baseValue;
      }
      this.toggleAttribute('data-free', Boolean(bundle && isFree));
      const subtext = this.querySelector('[data-bedding-totals]');
      const total = subtext?.querySelector('[data-bedding-total]');
      if (total) {
        const [base, twin, split] = subtext.dataset.beddingTotals.split('|');
        const amount = isSinglePillow ? twin : isSplitKing ? split : base;
        total.querySelector('[data-bedding-total-amount]').textContent = amount || '';
        total.hidden = !amount;
      }
      const prices = this.querySelector('[data-bedding-prices]');
      if (!prices) return;
      prices.hidden = !bundle;
      if (!bundle) return;
      const offers = this.data.offers;
      const offer = isFree ? this.data.freeOffer.label : offers[normalized] ?? offers.standard;
      this.querySelector('[data-bedding-regular]').textContent = bundle.priceText;
      this.querySelector('[data-bedding-offer-price]').textContent = offer;
    }

    normalizeSize(value) {
      const size = String(value || '').trim().toLowerCase();
      return ({ twinxl: 'twin xl', 'cal king': 'california king' })[size] || size;
    }

    resolve() {
      const form = document.getElementById(this.dataset.formId);
      const id = form?.querySelector('[name="id"]')?.value;
      const mattress = this.data.mattress.find((variant) => String(variant.id) === String(id));
      const size = String(mattress?.size || '').trim();
      const matches = this.data.bundle.filter((variant) => this.normalizeSize(variant.size) === this.normalizeSize(size));
      const bundle = matches.length === 1 ? matches[0] : null;
      const freeOffer = this.data.freeOffer;
      const isFree = Boolean(freeOffer?.enabled && Number.isFinite(freeOffer.threshold) && freeOffer.threshold >= 0 && mattress?.price > freeOffer.threshold);
      const valid = Boolean(mattress && size && bundle?.available && form && theme.settings.afterAtc !== 'no-js');
      const offerPrice = isFree ? freeOffer.label : this.data.offers?.[this.normalizeSize(size)] ?? this.data.offers?.standard;
      const state = [form, id, size, bundle?.id, bundle?.priceText, isFree, offerPrice, valid];
      if (this.renderedState?.every((value, index) => value === state[index])) {
        return valid ? { id: bundle.id, size, parent: String(id) } : null;
      }
      this.updatePrices(bundle, size, isFree);
      if (!valid) {
        this.disable();
        this.renderedState = state;
        return null;
      }
      if (!isFree && this.wasFree === true) this.toggle.checked = false;
      this.wasFree = isFree;
      this.toggle.disabled = false;
      this.hidden = false;
      this.querySelector('[data-bedding-message]').hidden = true;
      this.renderedState = state;
      this.syncAddButton();
      this.syncDrawer?.();
      return { id: bundle.id, size, parent: String(id) };
    }

    getItem(form) {
      if (form.getAttribute('id') !== this.dataset.formId || form.closest('quick-add-drawer, .js-quickbuy')) return null;
      try {
        const match = this.resolve();
        if (!match || !this.toggle.checked) return null;
        return {
          id: match.id,
          properties: {
            _ornate_bedding_offer: 'true',
            _ornate_bedding_size: match.size,
            _ornate_parent_variant: match.parent
          }
        };
      } catch (error) {
        return this.disable();
      }
    }
  }
  customElements.define('bedding-offer', BeddingOffer);
}
